import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useSites } from '../hooks/useSites';
import { useLogout } from '../hooks/useAuth';
import { MODULE_REGISTRY } from '@inboundhost/shared';

export function DashboardLayout() {
  const { data, isLoading } = useSites();
  const logout = useLogout();
  const navigate = useNavigate();

  const site = data?.sites[0];

  const enabledTypes = new Set(site?.modules.filter((m) => m.enabled).map((m) => m.type) ?? []);
  const availableModules = Object.values(MODULE_REGISTRY).filter(
    (def) => def.type !== 'business-info' && !enabledTypes.has(def.type),
  );

  return (
    <div className="min-h-screen flex">
      <aside className="w-64 bg-white border-r border-gray-200 flex flex-col">
        <div className="px-4 py-4 border-b border-gray-200">
          <Link to="/app" className="font-semibold">
            InboundHost
          </Link>
          {site && <div className="text-xs text-gray-500 mt-1">{site.subdomain}</div>}
        </div>
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-auto">
          <NavItem to="/app" end>
            Overview
          </NavItem>
          <NavItem to="/app/media">Media Library</NavItem>
          <NavItem to="/app/settings">Site settings</NavItem>
          <div className="pt-4 pb-1 px-2 text-xs uppercase tracking-wide text-gray-400">Sections</div>
          {site?.modules
            .filter((m) => m.enabled && m.type !== 'business-info')
            .map((m) => {
              const def = MODULE_REGISTRY[m.type as keyof typeof MODULE_REGISTRY];
              return (
                <NavItem key={m.type} to={`/app/modules/${m.type}`}>
                  {def?.label ?? m.type}
                </NavItem>
              );
            })}
          {availableModules.length > 0 && (
            <>
              <div className="pt-4 pb-1 px-2 text-xs uppercase tracking-wide text-gray-400">Add a section</div>
              {availableModules.map((def) => (
                <NavItem key={def.type} to={`/app/modules/${def.type}`}>
                  <span className="text-gray-400">+ </span>
                  {def.label}
                </NavItem>
              ))}
            </>
          )}
        </nav>
        <div className="px-4 py-3 border-t border-gray-200">
          <button
            type="button"
            onClick={() => logout.mutate(undefined, { onSuccess: () => navigate('/login') })}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            Log out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        {isLoading ? <div className="p-8 text-gray-500">Loading…</div> : <Outlet />}
      </main>
    </div>
  );
}

function NavItem({
  to,
  end,
  children,
}: {
  to: string;
  end?: boolean;
  children: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `block px-3 py-2 rounded text-sm ${isActive ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-100'}`
      }
    >
      {children}
    </NavLink>
  );
}
